import React from 'react';
import { MAP_CARDS_PORTAL_ID } from '@shared/components/JoyInteractableMap';
import { pxToRem } from '@withjoy/joykit/theme';
import { Flex, TextV2 } from '@withjoy/joykit';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { AccommodationPropertyWithCalculatedPrice } from '@shared/hooks/useSearchExtendedAccommodationProperties';
import { MapPinWithCardV2 } from '@shared/components/JoyInteractableMap/MapPin';
import { HotelCardTile } from './HotelCardTile';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { AiBorderBoxProps } from '@shared/components/AiBorderBox';
import { HotelProduct } from '@withjoy/joykit/icons';

interface HotelCardProps {
  location: google.maps.LatLngLiteral;
  forceUpdate?: string;
  property: AccommodationPropertyWithCalculatedPrice;
  intent?: AiBorderBoxProps['intent'];
  activePinId?: string;
  onClickHotelPin: (id: string, location: google.maps.LatLngLiteral, show: boolean) => void;
}

export const HotelCard = (props: HotelCardProps) => {
  const { location, forceUpdate, property, intent, activePinId, onClickHotelPin } = props;

  const isMobileOrTablet = useIsMobileOrTablet();

  const hasCard = property.lat && property.lng;
  const isCardVisible = activePinId === property.joyPlaceId;

  const onToggleCard = useEventCallback((show: boolean) => {
    onClickHotelPin(property.joyPlaceId, location, show);
  });

  return (
    <MapPinWithCardV2
      portalContainer={document.getElementById(MAP_CARDS_PORTAL_ID) as HTMLElement}
      cardStyleOverride={{
        ...(isMobileOrTablet && {
          bottom: isCardVisible ? 72 : 52,
        }),
        width: pxToRem(280),
        height: 'auto',
        padding: pxToRem(14),
        borderRadius: pxToRem(14),
        gap: pxToRem(14),
        flexDirection: 'column',
      }}
      position={{ lat: location.lat, lng: location.lng }}
      isActive={false}
      showOnTop={intent !== 'noBorder'}
      isCardVisible={isCardVisible}
      onClick={() => {
        onToggleCard(true);
      }}
      onDoubleClick={e => {
        e.stopPropagation();
      }}
      forceUpdate={forceUpdate || isMobileOrTablet.toString()}
      label={
        <>
          {property.price ? (
            <Flex
              gap={2}
              paddingX={1}
            >
              {property.strikeOutPrice && (
                <TextV2
                  typographyVariant={'body1'}
                  fontSize={pxToRem(12)}
                  fontWeight={500}
                  textDecorationLine={'line-through'}
                  color={'mono8'}
                >
                  {property.strikeOutPrice}
                </TextV2>
              )}
              <TextV2
                typographyVariant={'body1'}
                fontSize={pxToRem(12)}
                fontWeight={600}
              >
                {property.price}
              </TextV2>
            </Flex>
          ) : (
            <HotelProduct size={24} />
          )}
        </>
      }
      intent={intent}
    >
      {hasCard && (
        <HotelCardTile
          {...property}
          onCloseCard={() => onToggleCard(false)}
        />
      )}
    </MapPinWithCardV2>
  );
};
